import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import type { PurchaseOrderDetail } from '@/api/admin-inventory-types'
import { useCancelPurchaseOrder, useMarkPurchaseOrderOrdered, useReceivePurchaseOrder } from '@/api/api-mutations'
import { preloadGetPurchaseOrder } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Input } from '@/components/base/input'
import { usePrompt } from '@/components/base/prompt'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { RouteModal, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { formatUnitCost, purchaseOrderStatusColor } from '../-status'

export const Route = createFileRoute('/_authenticated/_general/inventory/receiving/$purchaseOrderUuid')({
	component: PurchaseOrderDetailRoute,
	loader: async ({ context, params }) => {
		await preloadGetPurchaseOrder(context, { path: { purchaseOrderUuid: params.purchaseOrderUuid } })
	},
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.PurchaseOrder()
	}
})

function PurchaseOrderDetailRoute() {
	const { purchaseOrderUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api => api.getPurchaseOrder({ path: { purchaseOrderUuid } })}
		>
			{data => <PurchaseOrderDetailDrawer po={data} />}
		</RouteModal>
	)
}

function PurchaseOrderDetailDrawer({ po }: { po: PurchaseOrderDetail }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const prompt = usePrompt()
	const receivePo = useReceivePurchaseOrder(true)
	const markOrdered = useMarkPurchaseOrderOrdered(true)
	const cancelPo = useCancelPurchaseOrder(true)

	const [receiveNow, setReceiveNow] = useState<Record<string, string>>({})
	const isReceivable = po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED'

	useEffect(() => {
		if (!isReceivable) {
			setReceiveNow({})
			return
		}
		const defaults: Record<string, string> = {}
		for (const line of po.lines) {
			const remaining = line.orderedQuantity - line.receivedQuantity
			defaults[line.uuid] = remaining > 0 ? String(remaining) : '0'
		}
		setReceiveNow(defaults)
	}, [po.uuid, po.status, isReceivable, po.lines])

	const handleReceive = async () => {
		const lines = po.lines
			.map(line => ({ lineUuid: line.uuid, add: Number(receiveNow[line.uuid] ?? '0') }))
			.filter(l => l.add > 0)
			.map(l => {
				const line = po.lines.find(x => x.uuid === l.lineUuid)!
				return { lineUuid: l.lineUuid, receivedQuantity: line.receivedQuantity + l.add }
			})
		if (lines.length === 0) return
		await receivePo.mutateAsync({ path: { purchaseOrderUuid: po.uuid }, body: { lines } })
		toast.success(L.Inventory.ReceivePurchaseOrder())
		handleSuccess()
	}

	const handleCancel = async () => {
		const confirmed = await prompt({
			title: L.General.AreYouSure(),
			description: L.Inventory.CancelOrder()
		})
		if (!confirmed) return
		await cancelPo.mutateAsync({ path: { purchaseOrderUuid: po.uuid } })
		toast.success(L.Inventory.CancelOrder())
		handleSuccess()
	}

	const busy = receivePo.isPending || markOrdered.isPending || cancelPo.isPending

	return (
		<>
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>
						{po.referenceNumber
							? `${L.Inventory.PurchaseOrder()} · ${po.referenceNumber}`
							: L.Inventory.PurchaseOrder()}
					</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<div className='flex flex-col gap-4'>
					<div className='flex items-center justify-between'>
						<Text className='text-sm text-ui-fg-subtle'>
							{po.inventoryLocation.name}
							{po.supplier ? ` · ${po.supplier.name}` : ''}
						</Text>
						<StatusCell color={purchaseOrderStatusColor(po.status)}>
							{L.Inventory.PurchaseOrderStatus[po.status]()}
						</StatusCell>
					</div>
					<div className='flex flex-col gap-2'>
						<div className='grid grid-cols-12 gap-2 text-xs font-medium text-ui-fg-subtle'>
							<div className='col-span-5'>{L.Inventory.Column.Product()}</div>
							<div className='col-span-2 text-right'>{L.Inventory.Column.Ordered()}</div>
							<div className='col-span-2 text-right'>{L.Inventory.Column.Received()}</div>
							<div className='col-span-3 text-right'>
								{isReceivable ? L.Inventory.Column.ReceiveNow() : L.Inventory.Column.UnitCost()}
							</div>
						</div>
						{po.lines.map(line => (
							<div key={line.uuid} className='grid grid-cols-12 items-center gap-2 text-sm'>
								<div className='col-span-5 min-w-0'>
									<div className='truncate'>{line.variant.productTitle}</div>
									{line.variant.sku && (
										<div className='truncate text-xs text-ui-fg-muted'>{line.variant.sku}</div>
									)}
								</div>
								<div className='col-span-2 text-right'>{line.orderedQuantity}</div>
								<div className='col-span-2 text-right'>{line.receivedQuantity}</div>
								<div className='col-span-3 text-right'>
									{isReceivable ? (
										<Input
											type='number'
											step='any'
											className='text-right'
											value={receiveNow[line.uuid] ?? ''}
											onChange={e =>
												setReceiveNow(prev => ({ ...prev, [line.uuid]: e.target.value }))
											}
										/>
									) : (
										formatUnitCost(line, po.currencyCode)
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex flex-wrap items-center justify-end gap-2'>
					{po.status === 'DRAFT' && (
						<>
							<Button variant='danger' size='small' disabled={busy} onClick={handleCancel}>
								{L.Inventory.CancelOrder()}
							</Button>
							<Button
								size='small'
								disabled={busy}
								onClick={async () => {
									await markOrdered.mutateAsync({ path: { purchaseOrderUuid: po.uuid } })
									toast.success(L.Inventory.MarkOrdered())
								}}
							>
								{L.Inventory.MarkOrdered()}
							</Button>
						</>
					)}
					{isReceivable && (
						<>
							<Button variant='danger' size='small' disabled={busy} onClick={handleCancel}>
								{L.Inventory.CancelOrder()}
							</Button>
							<Button size='small' disabled={busy} onClick={handleReceive}>
								{L.Inventory.ReceivePurchaseOrder()}
							</Button>
						</>
					)}
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Close()}
						</Button>
					</Drawer.Close>
				</div>
			</Drawer.Footer>
		</>
	)
}
